import React, { useEffect } from "react";
import { Button, Form, FormInstance, Radio, Popover } from "antd";
import { QuestionCircleFilled } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import { PrintWaybillFormValues, ShippingAddressMatchRule } from "../PrintWaybillForm";
import { MultiShopSelectPlatformType } from "@/components/MultiShopSelect/types";
import ShippingAddressSelect from "@/scenes/OrderManagement/components/select/ShippingAddressSelect";
import { useGlobalStore } from "@/store/hooks";
import { useModalStore } from "@/hooks/Modal/hooks";
import AddShippingAddressModal from "@/components/Modal/AddShippingAddressModal";
import { Link } from "react-router-dom";
import OrderDTO from "@/dto/order/OrderDTO";
import type SupplierPartnershipsStore from "@/store/SupplierPartnershipsStore";
import ShopType from "@/enum/ShopType";

const SHIPPING_ADDRESS_MATCH_RULE_STORAGE_KEY = "MULTI_PLATFORM_PRINT_SHIPPING_ADDRESS_MATCH_RULE";

function isShippingAddressMatchRule(value: string): value is ShippingAddressMatchRule {
    return [ShippingAddressMatchRule.SHOP, ShippingAddressMatchRule.TEMPLATE].includes(
        value as ShippingAddressMatchRule,
    );
}

interface ShippingAddressFormItemProps {
    firstPlatform: MultiShopSelectPlatformType;
    shopId?: number;
    defaultShippingAddressId?: number;
    onSippingAddressChange?(): void;
    form: FormInstance<PrintWaybillFormValues>;
    orders: OrderDTO[];
    supplierPartnershipsStore?: SupplierPartnershipsStore;
}

interface ShippingAddressFormItemExtraProps {
    form: FormInstance<PrintWaybillFormValues>;
    orders: OrderDTO[];
    supplierPartnershipsStore?: SupplierPartnershipsStore;
}

export const ShippingAddressFormItemExtra: React.FC<ShippingAddressFormItemExtraProps> = observer(function (props) {
    const { form, orders = [], supplierPartnershipsStore } = props;
    const globalStore = useGlobalStore();
    const modalStore = useModalStore();

    function handleOpenAddShipperModal() {
        modalStore.openModal(AddShippingAddressModal, {
            onSuccess() {
                globalStore.waybillPrintStore.loadShippingAddresses();
            },
            from: "打印弹窗",
        });
    }

    useEffect(() => {
        if (globalStore.waybillPrintStore.shippingAddressesLoading) return;

        const shippingAddressMatchRule = localStorage.getItem(SHIPPING_ADDRESS_MATCH_RULE_STORAGE_KEY);

        if (shippingAddressMatchRule && isShippingAddressMatchRule(shippingAddressMatchRule)) {
            form?.setFields([
                {
                    name: "shippingAddressMatchRule",
                    value: shippingAddressMatchRule,
                },
            ]);
        }
    }, [globalStore.waybillPrintStore.shippingAddressesLoading]);

    if (globalStore.waybillPrintStore.shippingAddressesLoading) {
        return null;
    }

    function handleShippingAddressMatchRuleChange(e: any) {
        const value = e.target.value;

        if (value) {
            localStorage.setItem(SHIPPING_ADDRESS_MATCH_RULE_STORAGE_KEY, value);
        } else {
            localStorage.removeItem(SHIPPING_ADDRESS_MATCH_RULE_STORAGE_KEY);
        }
    }

    function handleShippingAddressMatchRuleClick(value: ShippingAddressMatchRule) {
        if (form?.getFieldValue("shippingAddressMatchRule") !== value) return;

        form?.setFields([
            {
                name: "shippingAddressMatchRule",
                value: undefined,
            },
        ]);
        localStorage.removeItem(SHIPPING_ADDRESS_MATCH_RULE_STORAGE_KEY);
    }

    const hasSupplierShippingAddress = orders.some((order) => {
        const retailerShopId = order.supplierDropShippingInfo?.retailerShopId;
        if (!retailerShopId) return false;

        const partnerships = supplierPartnershipsStore?.supplierPartnershipsMap.get(order.shopId);
        const partnership = partnerships?.find((partnership) => partnership.shopId === retailerShopId);

        return !!partnership?.shippingAddress;
    });

    const hasOrderShippingAddress = orders.some((order) => {
        if (!order.sellerRemarks?.shippingAddressId) return false;

        const shop = globalStore.shopMapping.get(order.shopId);

        return [ShopType.MANUAL, ShopType.INDEPENDENT_MANUAL].includes(shop?.type);
    });

    return (
        <div>
            {globalStore.waybillPrintStore.shippingAddresses?.length === 0 ? (
                <div className="error">
                    目前没有发货人信息,请先
                    <Button onClick={handleOpenAddShipperModal} type="link">
                        点击这里添加发货人信息
                    </Button>
                </div>
            ) : (
                <div className="mt5">
                    {hasSupplierShippingAddress ? (
                        <div className="mb3">
                            【温馨提示】 有部分代理商设置了发货地址
                            <Link to="/partnership/supplier/myRetailers">(去查看)</Link>
                            ，则默认按代理商的发货地址打印，如有疑问可联系代理商咨询
                        </div>
                    ) : null}
                    {hasOrderShippingAddress ? <div>订单有发货人，则优先按订单的发货人打印</div> : null}
                    <Form.Item noStyle name="shippingAddressMatchRule" preserve={false}>
                        <Radio.Group onChange={handleShippingAddressMatchRuleChange}>
                            <Radio
                                value={ShippingAddressMatchRule.SHOP}
                                onClick={() => handleShippingAddressMatchRuleClick(ShippingAddressMatchRule.SHOP)}
                            >
                                不同店铺打印不同发货地址{" "}
                                <Link to="/setting/shippingAddress" onClick={(e) => e.stopPropagation()}>
                                    去设置
                                </Link>
                            </Radio>
                            <Radio
                                value={ShippingAddressMatchRule.TEMPLATE}
                                onClick={() => handleShippingAddressMatchRuleClick(ShippingAddressMatchRule.TEMPLATE)}
                            >
                                不同模板打印不同发货地址{" "}
                                <Link to="/setting/waybillTemplate" onClick={(e) => e.stopPropagation()}>
                                    去设置
                                </Link>
                            </Radio>
                        </Radio.Group>
                    </Form.Item>
                    <Popover
                        content={
                            <div>
                                <div>
                                    <span className="fb">不同店铺打印不同发货地址：</span>
                                    订单打印时优先使用店铺绑定的发货地址，没有则使用弹窗内选择的发货地址
                                </div>
                                <div>
                                    <span className="fb">不同模板打印不同发货地址：</span>
                                    订单打印时优先使用模板绑定的发货地址，没有则使用弹窗内选择的发货地址
                                </div>
                            </div>
                        }
                    >
                        <QuestionCircleFilled style={{ color: "#faad14", marginLeft: 6 }} />
                    </Popover>
                </div>
            )}
        </div>
    );
});

const ShippingAddressFormItem: React.FC<ShippingAddressFormItemProps> = (props) => {
    const {
        firstPlatform,
        shopId,
        defaultShippingAddressId,
        onSippingAddressChange,
        form,
        orders,
        supplierPartnershipsStore,
    } = props;

    return (
        <Form.Item<PrintWaybillFormValues>
            noStyle
            shouldUpdate={(prev: PrintWaybillFormValues, next: PrintWaybillFormValues) => {
                return prev[firstPlatform]?.template !== next[firstPlatform]?.template;
            }}
        >
            {() => {
                const values = form.getFieldsValue();

                const template = values[firstPlatform]?.template;

                return (
                    <>
                        <Form.Item
                            label="发货地址："
                            extra={
                                <ShippingAddressFormItemExtra
                                    form={form}
                                    orders={orders}
                                    supplierPartnershipsStore={supplierPartnershipsStore}
                                />
                            }
                            name="shippingAddress"
                            valuePropName={"checked"}
                            rules={[
                                {
                                    required: true,
                                    message: "发货地址不能为空!",
                                },
                            ]}
                        >
                            <ShippingAddressSelect
                                waybillTemplate={template}
                                shopId={shopId}
                                defaultShippingAddressId={defaultShippingAddressId}
                                onChange={onSippingAddressChange}
                                style={{ width: 645 }}
                            />
                        </Form.Item>
                    </>
                );
            }}
        </Form.Item>
    );
};

export default observer(ShippingAddressFormItem);
